import React from 'react';
import { formatCurrency } from '../../../utils/baseUtils';
import { availabilityCell, platformCell, utcDateCell, zonedDateTimeCell } from './finance-cells';

// The statement's columns (spec 2026-09-21 §5.3, frame 3a): the mock's 13
// plus Platform (owner, 2026-09-25) — 14 — as the base contract, plus CRV and
// Customer Fee only when a row in the period carries one, and the platform
// fee twice (paid in, taken out) only when a row carries one — hiding a
// figure that is inside Net Payout would make the row non-additive, and this
// screen refuses that. Extra Fee is always drawn: the mock lists it among
// the 13.
//
// Widths are the mock's pixel tracks as FLOORS: every column shares the
// card's spare width, Order taking the larger share. The mock drew fixed
// tracks for a 990px card; a fixed set on a wide screen handed every spare
// pixel to Order and pushed twelve figures against the right edge (owner,
// 2026-09-22). The template is built here rather than written as a
// Tailwind class because Tailwind cannot see a class assembled at runtime.
export const visibleOptionalColumns = (rows = []) => ({
  crvTax: rows.some((r) => r.crvTax > 0),
  customerFee: rows.some((r) => r.customerFee > 0),
  platformFee: rows.some((r) => r.platformFee > 0),
});

// Informational, not a link (mock 3a DEV NOTE, spec 2026-09-22 §7): the
// number is how a restaurant recognises the order on this statement, and a
// link out of a thirteen-column financial table invited a trip to a page
// that answers a different question. The payout detail page draws it the
// same way, for the same reason.
const orderCell = (row) => (
  <span className="font-semibold tabular-nums text-fin-ink">{`#${row.orderNumber}`}</span>
);

// Additive figures in ink, including $0.00; deductions in parentheses and
// muted, including ($0.00) — never blank. Net Payout is the server's
// figure (PayoutMath), never recomputed here; negative in parentheses, in ink
// like any other net (owner, 2026-10-12: red read as an alarm on a refunded
// order).
const additive = (value) => <span className="tabular-nums text-fin-ink">{formatCurrency(value || 0)}</span>;
const deduction = (value) => <span className="tabular-nums text-fin-faint">{`(${formatCurrency(value || 0)})`}</span>;
const net = (value) =>
  value < 0 ? (
    <span className="font-bold tabular-nums text-fin-ink">{`(${formatCurrency(Math.abs(value))})`}</span>
  ) : (
    <span className="font-bold tabular-nums text-fin-ink">{formatCurrency(value || 0)}</span>
  );

// A track that keeps the mock's width as its floor and grows with the card.
const track = (px, share = 1) => `minmax(${px}px,${share}fr)`;

const money = (key, label, px, kind) => ({
  key, label, width: track(px), align: 'right',
  render: (row) => (kind === 'deduction' ? deduction(row[key]) : kind === 'net' ? net(row[key]) : additive(row[key])),
});

// Owner, 2026-10-06: "Platform Fee (paid)" wrapped under itself in its
// column. Both columns are headed "Platform Fee" now, on one line, and each
// says which side of the row it is on behind a small info icon (`info`,
// drawn by the page with FinInfoPopover).
//
// A heading must fit its own track at the track's floor, or it wraps as soon
// as the card is narrow enough. Measured in Chrome at the header's 13.5px
// bold: "Platform Fee" with its icon is 100px, "Customer Fee" 90px and
// "Thisway Fee" 80px — the last two sat in 76px tracks and dropped "Fee" to
// a second line the same way.
const PLATFORM_FEE_INFO = {
  paid: 'Fee paid to Thisway by the customer. Counted here as part of what the customer paid.',
  out: 'Fee paid to Thisway by the customer. Taken out here, because it goes to Thisway.',
};

export const buildOrderReportColumns = (visible = {}) =>
  [
    { key: 'orderNumber', label: 'Order', width: track(68, 1.5), align: 'left', render: orderCell },
    { key: 'platform', label: 'Platform', width: track(76), align: 'left', render: (row) => platformCell(row.platform) },
    { key: 'createdAt', label: 'Created', width: track(58), align: 'left', render: (row) => zonedDateTimeCell(row.createdAt, row.timezoneId) },
    money('subtotal', 'Subtotal', 62),
    money('tax', 'Tax', 52),
    visible.crvTax && money('crvTax', 'CRV', 58),
    money('tip', 'Tip', 52),
    money('storeFees', 'Extra Fee', 64),
    // Owner, 2026-09-25: the platform fee is money the customer paid that
    // goes straight back out to Thisway, so it is drawn both ways — in, with
    // the figures the customer paid, and out, beside Thisway's own fee — and
    // the row adds up to Net. Both appear exactly when a row carries one.
    visible.platformFee && { ...money('platformFee', 'Platform Fee', 104), info: PLATFORM_FEE_INFO.paid },
    visible.customerFee && money('customerFee', 'Customer Fee', 92, 'deduction'),
    money('discount', 'Discount', 64, 'deduction'),
    money('cardFees', 'Card Fee', 64, 'deduction'),
    visible.platformFee && {
      ...money('platformFee', 'Platform Fee', 104, 'deduction'), key: 'platformFeeOut', info: PLATFORM_FEE_INFO.out,
    },
    money('thiswayFee', 'Thisway Fee', 84, 'deduction'),
    money('refund', 'Refund', 58, 'deduction'),
    // Explained behind the same small icon as Platform Fee (owner, 2026-10-07).
    {
      ...money('netPayout', 'Net Payout', 92, 'net'),
      info: 'Estimated amount payable to the restaurant after refunds, processing fees, platform fees, and other adjustments.',
    },
    { key: 'fundsAvailableOn', label: 'Available', width: track(62), align: 'left', render: (row) => utcDateCell(row.fundsAvailableOn) },
    { key: 'status', label: 'Status', width: track(78), align: 'left', render: (row) => availabilityCell(row.fundsAvailableOn) },
  ].filter(Boolean);

// The grid's own width: fixed tracks + 10px gaps + the row's px-5, so the
// card scrolls only when the viewport is narrower than that (1104px for the
// base 14 columns) — this page renders in the shell's full-width column
// (FinancePageShell's `column="full"`), which imposes no cap of its own.
const GAP = 10;
const ROW_PADDING = 40;
export const columnStyle = (columns) => {
  // Each track's floor is the px inside its minmax(), which parseInt cannot
  // read off the string on its own.
  const floor = (width) => parseInt((/(\d+)px/.exec(width) || [])[1], 10) || 68;
  const fixed = columns.reduce((sum, c) => sum + floor(c.width), 0);
  return {
    gridTemplateColumns: columns.map((c) => c.width).join(' '),
    minWidth: `${fixed + GAP * (columns.length - 1) + ROW_PADDING}px`,
  };
};
